import { FiltroDeTitulos, Titulo } from "@/models/financeiro/Titulo";

export function limparFiltroDosCamposDeTitulos(): FiltroDeTitulos {
	return {
		pessoa: '',
		tipoDeCliente: '',
		dataInicioPeriodo: '',
		dataFimPeriodo: '',
		formaDePagamento: '',
		numeroDocumento: '',
		dataInicioPagamento: '',
		dataFimPagamento: '',
		somenteComValorDesatualizado: false,
		somenteComErroDeRecalculo: false,
	}
}

export function calcularDataAposCarencia(titulo: Titulo, dataVencimento: Date): Date {
	const diasDeCarencia = titulo.diasDeCarencia || 0;

	const dataCarencia = new Date(
		dataVencimento.getFullYear(),
		dataVencimento.getMonth(),
		dataVencimento.getDate(),
	);

	if (diasDeCarencia > 0) {
		dataCarencia.setDate(dataCarencia.getDate() + diasDeCarencia);
	} else {
		const diaSemanaVencimento = dataCarencia.getDay();
		if (diaSemanaVencimento === 6) {
			dataCarencia.setDate(dataCarencia.getDate() + 2);
		} else if (diaSemanaVencimento === 0) {
			dataCarencia.setDate(dataCarencia.getDate() + 1);
		}
	}

	return new Date(
		dataCarencia.getFullYear(),
		dataCarencia.getMonth(),
		dataCarencia.getDate(),
	);
}


/**
 * Diz se o título ainda tem saldo a receber, ou seja, se ele pode entrar num pagamento.
 *
 * Este é o predicado ÚNICO da regra: ele decide quem aparece na grid do pagamento múltiplo,
 * quem pode nascer pré-selecionado e quem sobrevive quando a seleção é reconciliada depois
 * de uma busca. As três coisas precisam usar o mesmo critério, senão um título quitado some
 * da tela mas continua selecionado por baixo, soma no total e acaba recebendo uma baixa de
 * R$ 0,00.
 *
 * O saldo é o critério, e não a situação do título: existe título marcado como pago que
 * ainda tem saldo em aberto.
 *
 * Recebe o título BRUTO, nunca o formatado para exibição, cujos valores já são string.
 */
export function tituloTemSaldoAPagar(titulo: { valorAtualizado?: any; valor?: any }): boolean {
	return Number(titulo.valorAtualizado ?? titulo.valor) > 0
}

/**
 * Multa devida por um título vencido cujo campo de multa está zerado.
 *
 * Esse campo quase nunca é populado (448 de 452 títulos abertos e vencidos estavam com ele
 * zerado na base de dev), então a multa de título vencido é materializada aqui, a partir do
 * percentual configurado, e não lida do título.
 *
 * O que o campo zerado NÃO diz é o motivo de estar zerado. Ele fica assim tanto num título
 * que nunca teve multa lançada quanto num em que a multa foi PERDOADA por desconto numa
 * baixa anterior. Recriar a multa cheia no segundo caso cobra de novo o que já foi perdoado,
 * que era o defeito relatado. Por isso o valor perdoado entra como parâmetro e é abatido.
 *
 * O abatimento é feito aqui, sobre o valor RECRIADO, e nunca depois, sobre um valor que veio
 * do backend: aquele já chega líquido, e abater de novo seria subtrair duas vezes.
 *
 * Estorno não é afetado. Como o estorno é lançamento compensatório negativo e não exclusão,
 * a soma do perdoado volta a zero sozinha e a multa continua sendo recriada por inteiro.
 */
export function calcularMultaDeTituloVencido(
	porcentagemDeMulta: number,
	valorTitulo: number,
	multaJaPerdoada = 0,
): number {
	if (!porcentagemDeMulta || !valorTitulo) return 0

	const multaCheia = Number(
		(Number(porcentagemDeMulta) / 100 * Number(valorTitulo)).toFixed(2),
	)

	return Math.max(0, Number((multaCheia - Number(multaJaPerdoada || 0)).toFixed(2)))
}
